'use client'

import { asset } from '@/lib/assets'
import { PAYMENTS_ENABLED } from '@/lib/config'
import { breakdownFromExclusiveBase } from '@/lib/inclusiveGstBreakdown'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'

type PaymentRequiredModalProps = {
  isOpen: boolean
  paidAttemptsRemaining?: number
  onUnlock: () => void
  onCancel: () => void
  isProcessing?: boolean
  productPrice?: number | null
  productName?: string | null
}

export function PaymentRequiredModal({ isOpen, paidAttemptsRemaining = 0, onUnlock, onCancel, isProcessing = false, productPrice, productName }: PaymentRequiredModalProps) {
  // Freeze the page behind the paywall wherever this modal is mounted. Must run before the
  // early return so the hook order stays stable when the modal opens/closes.
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  const priceBreakdown =
    productPrice != null && productPrice > 0 ? breakdownFromExclusiveBase(productPrice) : null

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-body">
          <img
            src={asset("/assets/gifs/payment_required.gif")}
            alt="Payment Required"
            className={`modal-icon ${isProcessing ? 'faded' : ''}`}
          />

          <div className="modal-text-content">
            <h2 className={`modal-title ${isProcessing ? 'faded' : ''}`}>Interview plan limit reached</h2>

            <p className={`modal-description ${isProcessing ? 'faded' : ''}`}>
              {productName
                ? `Payment is required to access ${productName}.`
                : "You've used all of your free interview attempts. To continue practicing and improving your skills, please make a quick payment to unlock more attempts."}
            </p>

            {PAYMENTS_ENABLED && productPrice && productName && priceBreakdown && (
              <div className={`product-info ${isProcessing ? 'faded' : ''}`}>
                <p className="product-name">{productName}</p>
                <div className="price-breakdown" aria-label="Price breakdown excluding GST">
                  <div className="price-row">
                    <span className="price-row-label">Base price (excl. GST)</span>
                    <span className="price-row-value">₹{priceBreakdown.baseExcludingGst.toFixed(2)}</span>
                  </div>
                  <div className="price-row">
                    <span className="price-row-label">GST ({priceBreakdown.gstRatePercent}%)</span>
                    <span className="price-row-value">₹{priceBreakdown.gstAmount.toFixed(2)}</span>
                  </div>
                  <div className="price-divider" />
                  <div className="price-row price-row-total">
                    <span className="price-row-label">Total payable</span>
                    <span className="price-row-value product-price">₹{priceBreakdown.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {isProcessing && (
              <div className="processing-content">
                <div className="spinner-large"></div>
                <p className="status-text">Processing payment...</p>
                <p className="instruction-text">Please complete the payment in the popup window</p>
              </div>
            )}

            <div className={`modal-buttons ${isProcessing ? 'faded' : ''}`}>
              <button className="button-unlock" onClick={onUnlock} disabled={isProcessing}>
                Unlock & Continue
              </button>
              <button className="button-cancel" onClick={onCancel} disabled={isProcessing}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow-y: auto;
          z-index: 1000;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          padding: 3rem;
          max-width: 580px;
          width: 90%;
          margin: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-body {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
        }

        .modal-icon {
          width: 80px;
          height: 80px;
          margin-bottom: 2rem;
          transition: opacity 0.3s ease;
        }

        .modal-icon.faded {
          opacity: 0.4;
        }

        .modal-text-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .modal-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #5B4EF5;
          margin: 0 0 1.25rem 0;
          line-height: 1.3;
          transition: opacity 0.3s ease;
        }

        .modal-title.faded {
          opacity: 0.4;
        }

        .modal-description {
          font-size: 1rem;
          color: #6B7280;
          line-height: 1.6;
          margin: 0 0 1.5rem 0;
          max-width: 460px;
          transition: opacity 0.3s ease;
        }

        .modal-description.faded {
          opacity: 0.4;
        }

        .processing-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          margin: 2rem 0;
          width: 100%;
        }

        .spinner-large {
          width: 56px;
          height: 56px;
          border: 5px solid #E5E7EB;
          border-top-color: #5B4EF5;
          border-right-color: #6B5CE7;
          border-bottom-color: #5B4EF5;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 1.5rem;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .status-text {
          font-size: 1rem;
          font-weight: 700;
          color: #374151;
          margin: 0 0 0.5rem 0;
          text-align: center;
        }

        .instruction-text {
          font-size: 0.9375rem;
          color: #374151;
          margin: 0;
          text-align: center;
        }

        .faded {
          opacity: 0.4;
          transition: opacity 0.3s ease;
        }

        .paid-attempts {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 2rem;
          font-size: 0.9375rem;
          color: #374151;
          transition: opacity 0.3s ease;
        }

        .paid-attempts.faded {
          opacity: 0.4;
        }

        .product-info {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #F9FAFB;
          border-radius: 12px;
          width: 100%;
          transition: opacity 0.3s ease;
        }

        .product-info.faded {
          opacity: 0.4;
        }

        .product-name {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 0.5rem 0;
        }

        .price-breakdown {
          width: 100%;
          margin-top: 0.25rem;
        }

        .price-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 1rem;
          font-size: 0.9375rem;
          color: #4b5563;
          margin: 0.5rem 0 0 0;
        }

        .price-row:first-of-type {
          margin-top: 0;
        }

        .price-row-label {
          flex: 1;
          min-width: 0;
        }

        .price-row-value {
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }

        .price-divider {
          height: 1px;
          background: #e5e7eb;
          margin: 0.75rem 0;
        }

        .price-row-total {
          margin-top: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
        }

        .product-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: #5b4ef5;
          margin: 0;
        }

        .modal-buttons {
          display: flex;
          gap: 1rem;
          width: 100%;
          transition: opacity 0.3s ease;
        }

        .modal-buttons.faded {
          opacity: 0.4;
          pointer-events: none;
        }

        .button-unlock {
          flex: 1;
          padding: 0.875rem 2rem;
          background: linear-gradient(135deg, #6B5CE7 0%, #5B4EF5 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(91, 78, 245, 0.3);
        }

        .button-unlock:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(91, 78, 245, 0.4);
          background: linear-gradient(135deg, #7C6EE8 0%, #6B5CE7 100%);
        }

        .button-unlock:active {
          transform: translateY(0);
        }

        .button-unlock:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .button-unlock:disabled:hover {
          transform: none;
          box-shadow: 0 4px 12px rgba(91, 78, 245, 0.3);
        }

        .button-cancel:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button-cancel {
          flex: 1;
          padding: 0.875rem 2rem;
          background: white;
          color: #6B7280;
          border: 2px solid #E5E7EB;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .button-cancel:hover {
          border-color: #D1D5DB;
          color: #4B5563;
          background: #F9FAFB;
        }

        .button-cancel:active {
          transform: scale(0.98);
        }

        @media (max-width: 640px) {
          .modal-overlay {
            padding: 1rem;
          }

          .modal-content {
            padding: 2rem 1.5rem;
            max-width: 95%;
          }

          .modal-icon {
            width: 60px;
            height: 60px;
          }

          .modal-title {
            font-size: 1.5rem;
          }

          .modal-description {
            font-size: 0.9375rem;
          }

          .modal-buttons {
            flex-direction: column;
            max-width: 100%;
          }

          .button-unlock,
          .button-cancel {
            width: 100%;
            padding: 0.875rem 1.5rem;
          }
        }
      `}</style>
    </div>
  )
}
